import { useAuth } from '../contexts/AuthContext';
import { useDevice } from '../contexts/DeviceContext';
import { useLoginPreferenceToggle } from '../hooks/useLoginPreferenceToggle';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link, Navigate } from 'react-router-dom';
import { formatDateISO } from '../lib/dateUtils';

interface DashboardStats {
  totalSessions: number;
  completedSessions: number;
  inProgressSessions: number;
  draftSessions: number;
  totalFiles: number;
  totalTrainees: number;
  pilotAssessments: number;
  instructorAssessments: number;
}

interface RecentSession {
  id: string;
  session_date: string;
  aircraft_type: string;
  status: string;
  trainee: { full_name: string } | null;
}

interface PendingAction {
  type: 'missing_files' | 'draft_sessions' | 'pending_approval';
  count: number;
  message: string;
  icon: string;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { isMobile } = useDevice();
  const { prefersDashboard, handleToggle: handleToggleLoginPage, toastMessage: loginToggleToast } = useLoginPreferenceToggle();
  const [stats, setStats] = useState<DashboardStats>({
    totalSessions: 0,
    completedSessions: 0,
    inProgressSessions: 0,
    draftSessions: 0,
    totalFiles: 0,
    totalTrainees: 0,
    pilotAssessments: 0,
    instructorAssessments: 0,
  });
  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [loading, setLoading] = useState(true);

  // Get user metadata
  const userRole = user?.user_metadata?.role || 'Unknown';
  const userName = user?.user_metadata?.full_name || user?.email || 'User';

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Load statistics
      await loadStats();
      // Load recent sessions
      await loadRecentSessions();
      // Load pending actions
      await loadPendingActions();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Get sessions based on role
      let sessionsQuery = supabase.from('sessions').select('status, id');
      
      if (userRole === 'instructor') {
        sessionsQuery = sessionsQuery.eq('instructor_id', user?.id);
      } else if (userRole === 'trainee') {
        sessionsQuery = sessionsQuery.eq('trainee_id', user?.id);
      }

      const { data: sessions } = await sessionsQuery;

      // Count by status
      const totalSessions = sessions?.length || 0;
      const completedSessions = sessions?.filter(s => s.status === 'completed').length || 0;
      const inProgressSessions = sessions?.filter(s => s.status === 'in_progress').length || 0;
      const draftSessions = sessions?.filter(s => s.status === 'draft').length || 0;

      // Get files count
      let filesQuery = supabase.from('files').select('id', { count: 'exact', head: true });
      if (userRole !== 'admin') {
        filesQuery = filesQuery.eq('uploaded_by', user?.id);
      }
      const { count: filesCount } = await filesQuery;

      // Get trainees count (for instructors)
      let traineesCount = 0;
      if (userRole === 'instructor') {
        const { data: uniqueTrainees } = await supabase
          .from('sessions')
          .select('trainee_id')
          .eq('instructor_id', user?.id);
        
        const uniqueTraineeIds = new Set(uniqueTrainees?.map(s => s.trainee_id) || []);
        traineesCount = uniqueTraineeIds.size;
      }

      // Get assessment counts (parallel queries for better performance)
      let pilotAssessmentsCount = 0;
      let instructorAssessmentsCount = 0;

      if (sessions && sessions.length > 0) {
        const sessionIds = sessions.map(s => s.id);

        // Run both assessment queries in parallel
        const [pilotAssessmentsRes, instructorAssessmentsRes] = await Promise.all([
          supabase
            .from('assessments_pilot')
            .select('session_id, pilot_role')
            .in('session_id', sessionIds),
          supabase
            .from('assessments_instructor')
            .select('session_id')
            .in('session_id', sessionIds),
        ]);

        const uniquePilotSessions = new Set(
          pilotAssessmentsRes.data?.map(a => `${a.session_id}-${a.pilot_role || 'first_officer'}`) || []
        );
        pilotAssessmentsCount = uniquePilotSessions.size;

        const uniqueInstructorSessions = new Set(instructorAssessmentsRes.data?.map(a => a.session_id) || []);
        instructorAssessmentsCount = uniqueInstructorSessions.size;
      }

      setStats({
        totalSessions,
        completedSessions,
        inProgressSessions,
        draftSessions,
        totalFiles: filesCount || 0,
        totalTrainees: traineesCount,
        pilotAssessments: pilotAssessmentsCount,
        instructorAssessments: instructorAssessmentsCount,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRecentSessions = async () => {
    try {
      let query = supabase
        .from('sessions')
        .select(`
          id,
          session_date,
          aircraft_type,
          status,
          trainee:trainee_id(full_name),
          session_crew(crew_role, users:user_id(full_name), org_pilots:org_pilot_id(full_name))
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (userRole === 'instructor') {
        query = query.eq('instructor_id', user?.id);
      } else if (userRole === 'trainee') {
        query = query.eq('trainee_id', user?.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformedSessions = (data || []).map((session: any) => {
        const traineeFromJoin = Array.isArray(session.trainee) ? session.trainee[0] : session.trainee;
        if (traineeFromJoin) return { ...session, trainee: traineeFromJoin };

        // Fallback: resolve trainee from session_crew
        const foCrew = (session.session_crew || []).find((m: any) => m.crew_role === 'first_officer');
        const foName = foCrew?.users?.full_name || foCrew?.org_pilots?.full_name;
        return { ...session, trainee: foName ? { full_name: foName } : null };
      });

      setRecentSessions(transformedSessions);
    } catch (error) {
      console.error('Error loading recent sessions:', error);
    }
  };

  const loadPendingActions = async () => {
    const actions: PendingAction[] = [];

    try {
      // Check for sessions without files
      let sessionsQuery = supabase
        .from('sessions')
        .select('id')
        .neq('status', 'completed')
        .neq('status', 'approved');

      if (userRole === 'instructor') {
        sessionsQuery = sessionsQuery.eq('instructor_id', user?.id);
      }

      const { data: sessionsWithoutFiles } = await sessionsQuery;

      if (sessionsWithoutFiles) {
        // Check which sessions have no files
        const sessionIds = sessionsWithoutFiles.map(s => s.id);
        const { data: filesData } = await supabase
          .from('files')
          .select('session_id');

        const sessionsWithFilesIds = new Set(filesData?.map(f => f.session_id) || []);
        const sessionsNeedingFiles = sessionIds.filter(id => !sessionsWithFilesIds.has(id));

        if (sessionsNeedingFiles.length > 0) {
          actions.push({
            type: 'missing_files',
            count: sessionsNeedingFiles.length,
            message: `${sessionsNeedingFiles.length} session${sessionsNeedingFiles.length > 1 ? 's' : ''} missing audio files`,
            icon: '📁',
          });
        }
      }

      // Check for draft sessions
      if (stats.draftSessions > 0) {
        actions.push({
          type: 'draft_sessions',
          count: stats.draftSessions,
          message: `${stats.draftSessions} draft session${stats.draftSessions > 1 ? 's' : ''} need${stats.draftSessions === 1 ? 's' : ''} completion`,
          icon: '✏️',
        });
      }

      // Check for sessions pending approval (instructors only)
      if (userRole === 'instructor') {
        const { data: pendingApproval } = await supabase
          .from('sessions')
          .select('id')
          .eq('instructor_id', user?.id)
          .eq('status', 'completed');

        if (pendingApproval && pendingApproval.length > 0) {
          actions.push({
            type: 'pending_approval',
            count: pendingApproval.length,
            message: `${pendingApproval.length} session${pendingApproval.length > 1 ? 's' : ''} ready for approval`,
            icon: '✅',
          });
        }
      }

      setPendingActions(actions);
    } catch (error) {
      console.error('Error loading pending actions:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-steel/20 text-slate';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-steel/20 text-slate';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
          <p className="mt-2 text-steel">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Mobile users go to /sessions unless they explicitly prefer the dashboard
  if (isMobile && !prefersDashboard) {
    return <Navigate to="/sessions" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className={`font-bold text-navy mb-1 ${isMobile ? 'text-xl' : 'text-3xl mb-2'}`}>
          Welcome back, {userName}!
        </h1>
        <p className={`text-steel ${isMobile ? 'text-sm' : ''}`}>
          Here's what's happening with your training today
        </p>
      </div>

      {/* Statistics Cards */}
      <div className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'}`}>
        {/* Total Sessions */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">Total Sessions</p>
              <p className="text-3xl font-bold text-navy mt-1">{stats.totalSessions}</p>
            </div>
            <div className="text-4xl">📝</div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-green-600 font-medium">{stats.completedSessions} completed</span>
          </div>
        </div>

        {/* In Progress */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">In Progress</p>
              <p className="text-3xl font-bold text-navy mt-1">{stats.inProgressSessions}</p>
            </div>
            <div className="text-4xl">🔄</div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-yellow-600 font-medium">{stats.draftSessions} drafts</span>
          </div>
        </div>

        {/* Files Uploaded */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">Files Uploaded</p>
              <p className="text-3xl font-bold text-navy mt-1">{stats.totalFiles}</p>
            </div>
            <div className="text-4xl">📁</div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-blue-600 font-medium">Audio & transcripts</span>
          </div>
        </div>

        {/* Trainees (Instructors) or Status (Trainees) */}
        {userRole === 'instructor' ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-steel">Active Trainees</p>
                <p className="text-3xl font-bold text-navy mt-1">{stats.totalTrainees}</p>
              </div>
              <div className="text-4xl">👥</div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-purple-600 font-medium">Under supervision</span>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-steel">Completed</p>
                <p className="text-3xl font-bold text-navy mt-1">{stats.completedSessions}</p>
              </div>
              <div className="text-4xl">✅</div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-green-600 font-medium">Training sessions</span>
            </div>
          </div>
        )}
      </div>

      {/* Assessment Statistics */}
      {(stats.pilotAssessments > 0 || stats.instructorAssessments > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pilot Assessments */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-md p-6 border-2 border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-steel mb-1">Pilot Assessments</p>
                <p className="text-3xl font-bold text-navy">{stats.pilotAssessments}</p>
              </div>
              <div className="text-4xl">✈️</div>
            </div>
            <div className="flex items-center text-sm">
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                ✓ Complete
              </span>
              <span className="ml-2 text-steel">
                {stats.pilotAssessments} of {stats.totalSessions} sessions
              </span>
            </div>
            <p className="text-xs text-steel mt-2">9 ICAO competencies evaluated</p>
          </div>

          {/* Instructor Assessments */}
          <div className="bg-gradient-to-br from-purple-50 to-white rounded-lg shadow-md p-6 border-2 border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-steel mb-1">Instructor Assessments</p>
                <p className="text-3xl font-bold text-navy">{stats.instructorAssessments}</p>
              </div>
              <div className="text-4xl">👨‍🏫</div>
            </div>
            <div className="flex items-center text-sm">
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                ✓ Complete
              </span>
              <span className="ml-2 text-steel">
                {stats.instructorAssessments} of {stats.totalSessions} sessions
              </span>
            </div>
            <p className="text-xs text-steel mt-2">5 IEC criteria evaluated</p>
          </div>
        </div>
      )}

             {/* Pending Actions Alerts */}
             {pendingActions.length > 0 && (
               <div className="bg-orca-vision/10 border-l-4 border-orca-vision rounded-lg shadow-md p-6">
                 <div className="flex items-start">
                   <div className="flex-shrink-0 text-3xl mr-4">⚠️</div>
                   <div className="flex-1">
                     <h3 className="text-lg font-heading font-semibold text-navy mb-3">
                       Action Required
                     </h3>
                     <ul className="space-y-2">
                       {pendingActions.map((action, index) => (
                         <li key={index} className="flex items-center text-navy">
                           <span className="mr-2">{action.icon}</span>
                           <span className="font-medium">{action.message}</span>
                         </li>
                       ))}
                     </ul>
                     <Link
                       to="/sessions"
                       className="inline-block mt-4 px-4 py-2 bg-orca-vision text-white rounded-md hover:bg-orca-vision/90 transition-colors text-sm font-semibold shadow-md"
                     >
                       View Sessions
                     </Link>
                   </div>
                 </div>
               </div>
             )}

      {/* Recent Sessions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-navy">Recent Sessions</h3>
          <Link
            to="/sessions"
            className="text-sm font-medium text-orca-elevate hover:text-orca-elevate/80 transition-colors"
          >
            View All →
          </Link>
        </div>

        {recentSessions.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg">
            <p className="text-steel mb-2">No sessions yet</p>
            <Link
              to="/sessions"
              className="text-sm text-orca-elevate hover:text-orca-elevate/80 font-medium"
            >
              Create your first session →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {recentSessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between p-4 border border-steel/20 rounded-md hover:bg-ice/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-1">
                    <span className="text-sm font-medium text-navy">
                      {formatDateISO(session.session_date)}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(
                        session.status
                      )}`}
                    >
                      {session.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-steel">
                    {session.trainee?.full_name || 'Unknown Trainee'} • {session.aircraft_type}
                  </p>
                </div>
                <Link
                  to={`/sessions/${session.id}`}
                  className="ml-4 px-3 py-1 text-sm font-medium text-orca-elevate hover:text-orca-elevate/80 transition-colors"
                >
                  View
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions (desktop only - mobile has bottom nav) */}
      <div className={`bg-white rounded-lg shadow-md p-6 ${isMobile ? 'hidden' : ''}`}>
        <h3 className="text-lg font-semibold text-navy mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/sessions"
            className="block p-4 border-2 border-orca-elevate/30 rounded-lg hover:border-orca-elevate hover:bg-orca-elevate/5 transition-colors"
          >
            <div className="text-3xl mb-2">📝</div>
            <h4 className="font-semibold text-navy mb-1">Create Session</h4>
            <p className="text-sm text-steel">Start a new training session</p>
          </Link>

          <Link
            to="/files"
            className="block p-4 border-2 border-steel/20 rounded-lg hover:border-orca-elevate hover:bg-orca-elevate/5 transition-colors"
          >
            <div className="text-3xl mb-2">📁</div>
            <h4 className="font-semibold text-navy mb-1">Upload Files</h4>
            <p className="text-sm text-steel">Upload audio or transcripts</p>
          </Link>

          <Link
            to="/sessions"
            className="block p-4 border-2 border-steel/20 rounded-lg hover:border-orca-elevate hover:bg-orca-elevate/5 transition-colors"
          >
            <div className="text-3xl mb-2">📊</div>
            <h4 className="font-semibold text-navy mb-1">View All Sessions</h4>
            <p className="text-sm text-steel">Browse training history</p>
          </Link>
        </div>
      </div>

      {/* Mobile: toggle login destination */}
      {isMobile && (
        <>
          <div className="text-center pt-2 pb-4">
            <button
              onClick={handleToggleLoginPage}
              className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
            >
              {prefersDashboard ? 'Show sessions on login' : 'Show dashboard on login'}
            </button>
          </div>
          {loginToggleToast && (
            <div className="fixed bottom-20 left-4 right-4 bg-navy text-white text-sm text-center py-3 px-4 rounded-xl shadow-lg z-50 animate-fade-in">
              {loginToggleToast}
            </div>
          )}
        </>
      )}
    </div>
  );
}

